import type { ReactNode } from 'react';

import styles from './AdminManagement.module.css';

export type AdminIconName =
  | 'arrow'
  | 'bank'
  | 'calendar'
  | 'check'
  | 'clock'
  | 'customer'
  | 'document'
  | 'download'
  | 'filter'
  | 'money'
  | 'package'
  | 'payment'
  | 'plan'
  | 'report'
  | 'search'
  | 'shield'
  | 'spark'
  | 'warning';

export function AdminIcon({ name }: { name: AdminIconName }): React.JSX.Element {
  const line = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'arrow':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M5 12h14m-5-5 5 5-5 5" />
        </svg>
      );
    case 'bank':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="m3 9 9-5 9 5M5 10v8m5-8v8m4-8v8m5-8v8M3 20h18" />
        </svg>
      );
    case 'calendar':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect {...line} x="3" y="5" width="18" height="16" rx="2" />
          <path {...line} d="M8 3v4m8-4v4M3 10h18" />
        </svg>
      );
    case 'check':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...line} cx="12" cy="12" r="9" />
          <path {...line} d="m8 12 2.5 2.5L16.5 9" />
        </svg>
      );
    case 'clock':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...line} cx="12" cy="12" r="9" />
          <path {...line} d="M12 7v5l3 2" />
        </svg>
      );
    case 'customer':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...line} cx="12" cy="8" r="4" />
          <path {...line} d="M4.5 21a7.5 7.5 0 0 1 15 0" />
        </svg>
      );
    case 'document':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M6 3h9l4 4v14H6zM15 3v5h4M9 12h7M9 16h5" />
        </svg>
      );
    case 'download':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M12 3v12m-5-5 5 5 5-5M5 20h14" />
        </svg>
      );
    case 'filter':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M4 6h16M7 12h10M10 18h4" />
        </svg>
      );
    case 'money':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect {...line} x="3" y="5" width="18" height="14" rx="2" />
          <path {...line} d="M3 10h18M7 15h4" />
        </svg>
      );
    case 'package':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="m12 3 8 4.5v9L12 21l-8-4.5v-9zM4 7.5l8 4.5 8-4.5M12 12v9" />
        </svg>
      );
    case 'payment':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect {...line} x="3" y="5" width="18" height="14" rx="2" />
          <path {...line} d="M3 10h18M7 15h4" />
        </svg>
      );
    case 'plan':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect {...line} x="4" y="3" width="16" height="18" rx="2" />
          <path {...line} d="M8 8h8M8 12h8M8 16h5" />
        </svg>
      );
    case 'report':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M5 20V10m7 10V4m7 16v-7" />
        </svg>
      );
    case 'search':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle {...line} cx="10.5" cy="10.5" r="6.5" />
          <path {...line} d="m16 16 5 5" />
        </svg>
      );
    case 'shield':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M12 3 20 6v5c0 5-3.4 8.4-8 10-4.6-1.6-8-5-8-10V6z" />
          <path {...line} d="m8.5 12 2.2 2.2 4.8-5" />
        </svg>
      );
    case 'spark':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="m13 2-8 12h7l-1 8 8-12h-7z" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path {...line} d="M12 3 2.5 20h19zM12 9v5m0 3h.01" />
        </svg>
      );
  }
}

export function ManagementHeader({
  eyebrow,
  title,
  subtitle,
  icon,
  context,
  action,
}: {
  eyebrow: string;
  title: string;
  subtitle: string;
  icon: AdminIconName;
  context: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <header className={styles.pageHeader}>
      <div>
        <p className={styles.eyebrow}>{eyebrow}</p>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {action ?? (
        <div className={styles.headerContext}>
          <span className={styles.iconTile}>
            <AdminIcon name={icon} />
          </span>
          <span>
            <strong>{context}</strong>
            <small>Live operational data</small>
          </span>
        </div>
      )}
    </header>
  );
}

export function MetricCard({
  label,
  value,
  note,
  icon,
  tone = 'green',
}: {
  label: string;
  value: ReactNode;
  note?: string;
  icon: AdminIconName;
  tone?: 'green' | 'blue' | 'amber' | 'violet';
}): React.JSX.Element {
  return (
    <article className={`${styles.metricCard} ${styles[`tone${tone}`]}`}>
      <span className={styles.metricIcon}>
        <AdminIcon name={icon} />
      </span>
      <span className={styles.metricCopy}>
        <small>{label}</small>
        <strong>{value}</strong>
        {note !== undefined && <span>{note}</span>}
      </span>
    </article>
  );
}

export function conciseId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 8).toUpperCase()}…` : value.toUpperCase();
}
export function formatAdminDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unavailable'
    : date.toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' });
}
export { styles as adminManagementStyles };
